import { AlertTriangle, Info, AlertOctagon } from "lucide-react";
import { Card, Badge } from "@/components/ui/primitives";
import type { RiskFindingDTO } from "@/lib/api";
import { ManualCitationLink } from "@/components/ManualCitationDialog";
import { cn } from "@/lib/utils";

export function RiskFindingCard({
  finding,
  animateIn,
  delayMs,
}: {
  finding: RiskFindingDTO;
  animateIn?: boolean;
  delayMs?: number;
}) {
  const { tone, toneBg, Icon } = (() => {
    switch (finding.severity) {
      case "critical":
        return { tone: "text-destructive", toneBg: "border-destructive/30 bg-destructive/5", Icon: AlertOctagon };
      case "warn":
        return { tone: "text-warn", toneBg: "border-warn/30 bg-warn/5", Icon: AlertTriangle };
      default:
        return { tone: "text-brand", toneBg: "border-brand/30 bg-brand/5", Icon: Info };
    }
  })();
  return (
    <Card
      className={cn(
        "p-5",
        toneBg,
        animateIn && "animate-in fade-in slide-in-from-bottom-2 duration-500",
      )}
      style={
        animateIn
          ? { animationDelay: `${delayMs ?? 0}ms`, animationFillMode: "both" }
          : undefined
      }
    >
      <div className="flex items-start gap-3">
        <Icon className={cn("h-5 w-5 mt-0.5", tone)} strokeWidth={1.9} />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant={finding.severity === "critical" ? "destructive" : finding.severity === "warn" ? "warn" : "default"}>
              {finding.severity === "warn" ? "Warning" : finding.severity.charAt(0).toUpperCase() + finding.severity.slice(1)}
            </Badge>
            <h3 className="text-sm font-semibold text-foreground">{finding.title}</h3>
          </div>
          <p className="text-sm text-muted-foreground">{finding.description}</p>
          {(finding.tier_before || finding.tier_after) && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground line-through">Tier {finding.tier_before}</span>
              <span className="text-muted-foreground">→</span>
              <span className="font-semibold text-foreground">Tier {finding.tier_after}</span>
            </div>
          )}
          {finding.citation_markdown && (
            <div>
              <ManualCitationLink
                anchor={finding.citation_anchor}
                title={finding.citation_title}
                bodyMarkdown={finding.citation_markdown}
              />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
